import React from 'react';
import {
  UserIcon,
  LocationMarkerIcon,
  CalendarIcon
} from '@heroicons/react/solid';

import { Fare, FareStatus } from '../api/fare';
import { FareClass, FareAgeGroup } from '../api/fareType';

import Badge from './Badge';

const classNames: Record<FareClass, string> = {
  [FareClass.Monthly]: '30-day pass',
  [FareClass.Yearly]: 'Yearly pass',
  [FareClass.GeneralLargeFamily]: 'General large family pass',
  [FareClass.SpecialLargeFamily]: 'Special large family pass',
  [FareClass.WithDisabilities]: 'Disabled persons pass',
  [FareClass.Unknown]: 'Unknown'
};

const ageGroupNames: Record<FareAgeGroup, string> = {
  [FareAgeGroup.Youth]: 'Youth',
  [FareAgeGroup.General]: 'General',
  [FareAgeGroup.Elderly]: 'Elderly',
  [FareAgeGroup.Unknown]: 'Unknown'
};

const statusBadges: Record<FareStatus, JSX.Element> = {
  [FareStatus.Inactive]: (
    <Badge className="bg-gray-100 text-gray-800">Inactive</Badge>
  ),
  [FareStatus.Active]: (
    <Badge className="bg-green-100 text-green-800">Active</Badge>
  ),
  [FareStatus.ExpiresSoon]: (
    <Badge className="bg-yellow-100 text-yellow-800">Soon</Badge>
  )
};

const dateFormatter = new Intl.DateTimeFormat();

type FareListEntryProps = {
  fare: Fare;
};

function FareListEntry({ fare }: FareListEntryProps): JSX.Element {
  const { type } = fare;
  const clazz = classNames[type.class];
  const ageGroup = ageGroupNames[type.ageGroup];
  const statusBadge = statusBadges[fare.status];
  const expiresOn = new Date(fare.expiresOn);

  return (
    <li className="block hover:bg-gray-50">
      <div className="px-4 py-4 sm:px-6">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-gray-900 truncate">{clazz}</p>
          <div className="ml-2 flex-shrink-0 flex">{statusBadge}</div>
        </div>
        <div className="mt-2 sm:flex sm:justify-between">
          <div className="sm:flex">
            <p className="flex items-center text-sm text-gray-500">
              <UserIcon
                className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
              {ageGroup}
            </p>
            <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
              <LocationMarkerIcon
                className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
              Region {type.region}
            </p>
          </div>
          <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
            <CalendarIcon
              className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
            <p>
              Expires on{' '}
              <time dateTime={expiresOn.toISOString()}>
                {dateFormatter.format(expiresOn)}
              </time>
            </p>
          </div>
        </div>
      </div>
    </li>
  );
}

export default React.memo(FareListEntry);
